import { relayScormRuntimeProgress, terminateScormRuntime } from './scormRuntime';

type CompletionStatus = 'incomplete' | 'completed';

interface ScormSessionData {
  token: string;
  lmsUserId: string;
}

interface LastRelayState {
  completionStatus: CompletionStatus;
  progressPercent?: number;
}

const SCORM_SESSION_KEY = 'student_scorm_session';
const SCORM_LAST_RELAY_KEY = 'student_scorm_last_relay';
const SCORM_COURSE_ENGAGED_KEY = 'student_scorm_course_engaged';

let unloadHandlerInstalled = false;

const readLastRelay = (): LastRelayState | null => {
  try {
    const raw = sessionStorage.getItem(SCORM_LAST_RELAY_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LastRelayState;
  } catch {
    return null;
  }
};

const writeLastRelay = (state: LastRelayState): void => {
  sessionStorage.setItem(SCORM_LAST_RELAY_KEY, JSON.stringify(state));
};

export const getScormSessionFromStorage = (): ScormSessionData | null => {
  try {
    const raw = localStorage.getItem(SCORM_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ScormSessionData;
    if (!parsed.token || !parsed.lmsUserId) return null;
    return parsed;
  } catch {
    return null;
  }
};

const shouldRelayProgress = (
  completionStatus: CompletionStatus,
  progressPercent?: number
): boolean => {
  const last = readLastRelay();
  if (!last) return true;
  return (
    last.completionStatus !== completionStatus ||
    last.progressPercent !== progressPercent
  );
};

const logScormRelayToBackend = async (
  session: ScormSessionData,
  completionStatus: CompletionStatus,
  details: string,
  progressPercent?: number
): Promise<void> => {
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  const token = localStorage.getItem('student_token');

  try {
    await fetch(`${apiBaseUrl}/scorm/progress/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'ngrok-skip-browser-warning': '1',
      },
      body: JSON.stringify({
        token: session.token,
        lmsUserId: session.lmsUserId,
        completionStatus,
        details,
        progressPercent,
      }),
    });
  } catch {
    await fetch(`${apiBaseUrl}/scorm/progress/update-failure`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'ngrok-skip-browser-warning': '1',
      },
      body: JSON.stringify({
        token: session.token,
        lmsUserId: session.lmsUserId,
        details,
      }),
    }).catch(() => undefined);
  }
};

export const markScormCourseEngaged = (): void => {
  sessionStorage.setItem(SCORM_COURSE_ENGAGED_KEY, '1');
};

const isScormCourseEngaged = (): boolean =>
  sessionStorage.getItem(SCORM_COURSE_ENGAGED_KEY) === '1';

export const relayScormProgressToLms = async (
  completionStatus: CompletionStatus,
  details: string,
  progressPercent?: number,
  options: { force?: boolean } = {}
): Promise<boolean> => {
  const session = getScormSessionFromStorage();
  if (!session) return false;

  let normalizedProgress =
    progressPercent !== undefined
      ? Math.max(0, Math.min(100, Math.round(progressPercent)))
      : undefined;

  // Until the learner opens the course, keep LMS progress at 0% for this session.
  if (!isScormCourseEngaged() && completionStatus !== 'completed') {
    normalizedProgress = 0;
  }

  if (!options.force && !shouldRelayProgress(completionStatus, normalizedProgress)) {
    return false;
  }

  try {
    relayScormRuntimeProgress({
      completionStatus,
      progressPercent: normalizedProgress,
      details,
    });
  } catch {
    // Backend audit log still provides traceability.
  }

  writeLastRelay({ completionStatus, progressPercent: normalizedProgress });
  await logScormRelayToBackend(session, completionStatus, details, normalizedProgress);
  return true;
};

/** First SCORM write as soon as the learner enters the LMS iframe (login tunnel). */
export const ensureScormLmsLink = async (details = 'SCORM session started'): Promise<void> => {
  sessionStorage.removeItem(SCORM_COURSE_ENGAGED_KEY);
  sessionStorage.removeItem(SCORM_LAST_RELAY_KEY);
  await relayScormProgressToLms('incomplete', details, 0, { force: true });
};

export const fetchCourseProgressPercent = async (courseId: string): Promise<number | null> => {
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  const token = localStorage.getItem('student_token');
  if (!token) return null;

  try {
    const response = await fetch(
      `${apiBaseUrl}/student/courses/${courseId}?compact=1`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'ngrok-skip-browser-warning': '1',
        },
      }
    );
    if (!response.ok) return null;
    const payload = await response.json();
    const progress = Number(payload?.progress);
    if (Number.isNaN(progress)) return null;
    return Math.max(0, Math.min(100, progress));
  } catch {
    return null;
  }
};

export const syncScormProgressToLms = async (
  courseId: string,
  details = 'SCORM progress sync'
): Promise<void> => {
  markScormCourseEngaged();
  const progress = await fetchCourseProgressPercent(courseId);
  const effectiveProgress = progress ?? 0;
  const completionStatus: CompletionStatus =
    effectiveProgress >= 100 ? 'completed' : 'incomplete';

  await relayScormProgressToLms(completionStatus, details, effectiveProgress);
};

const recommitLastScormProgress = (): void => {
  const last = readLastRelay();
  if (!last) return;

  relayScormRuntimeProgress({
    completionStatus: last.completionStatus,
    progressPercent: last.progressPercent,
    details: 'SCORM session closing',
  });
};

export const terminateScormLmsSession = (): void => {
  recommitLastScormProgress();
  terminateScormRuntime();
};

export const installScormSessionLifecycle = (): void => {
  if (unloadHandlerInstalled || typeof window === 'undefined') return;
  unloadHandlerInstalled = true;

  const handleExit = () => {
    if (!getScormSessionFromStorage()) return;
    terminateScormLmsSession();
  };

  window.addEventListener('pagehide', handleExit);
  window.addEventListener('beforeunload', handleExit);
};
